import { createContext, useMemo, useState } from "react";
import {
  createSingletonComponent,
  useSafeContext,
} from "@aviv-unified-frontend/internal";
import type { PropsWithChildren } from "react";
import type { ApplicationRegistryItem } from "@aviv-unified-frontend/registry";

import { IS_DEV_LIKE_ENVIRONMENT } from "../../constants/environment";
import type { PortalConfig } from "../../../configs/brands/types";

import type { Errors, InitialApplication } from "./types";
import { GLOBAL_KEY } from "./constants";

const INTERNAL_GLOBAL_KEY = Symbol(GLOBAL_KEY);

type CreateGlobalStoreContextInput = {
  initialApplication?: InitialApplication | undefined;
  headerFooterConfig?: PortalConfig | undefined;
  applicationId: ApplicationRegistryItem["id"] | undefined;
  errors?: Errors;
};

type CreateGlobalStoreContextOutput = CreateGlobalStoreContextInput & {
  setApplicationId: (id: ApplicationRegistryItem["id"]) => void;
};

const GlobalStoreContext = createContext<CreateGlobalStoreContextOutput | null>(
  null,
);

export const createGlobalStoreContext = ({
  initialApplication,
  headerFooterConfig,
  applicationId,
  errors,
}: CreateGlobalStoreContextInput) => {
  return {
    [INTERNAL_GLOBAL_KEY]: {
      initialApplication,
      headerFooterConfig,
      applicationId,
      errors,
    },
  };
};

type GlobalStoreProviderProps = PropsWithChildren<{
  context: ReturnType<typeof createGlobalStoreContext>;
}>;

const extractErrors = (errors: Errors) => {
  if (!IS_DEV_LIKE_ENVIRONMENT) return undefined;

  if (typeof errors !== "object") return undefined;

  if (Object.keys(errors).length > 0) return errors;

  return undefined;
};

export const GlobalStoreProvider =
  createSingletonComponent<GlobalStoreProviderProps>(
    "GlobalStoreProvider",
    ({ children, context }) => {
      const {
        headerFooterConfig,
        initialApplication,
        applicationId: initialApplicationId,
        errors,
      } = context[INTERNAL_GLOBAL_KEY];

      const [applicationId, setApplicationId] = useState<
        ApplicationRegistryItem["id"] | undefined
      >(initialApplicationId);

      const value = useMemo(() => {
        return {
          applicationId,
          headerFooterConfig,
          initialApplication,
          setApplicationId,
          errors: extractErrors(errors),
        };
      }, [
        headerFooterConfig,
        initialApplication,
        applicationId,
        setApplicationId,
        errors,
      ]);

      return (
        <GlobalStoreContext.Provider value={value}>
          {children}
        </GlobalStoreContext.Provider>
      );
    },
  );

export const useGlobalStore = () =>
  useSafeContext(
    {
      package: "@aviv-unified-frontend/branded-shell",
      module: "global",
    },
    GlobalStoreContext,
  );
